// ==================== TOGGLE/SWITCH COMPONENT ====================

import { forwardRef, useId } from 'react';
import { cn } from '../../utils/helpers';

interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'success';
  labelPosition?: 'left' | 'right';
}

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      variant = 'primary',
      labelPosition = 'right',
      className,
      disabled,
      checked,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const toggleId = id || generatedId;

    const sizes = {
      sm: {
        track: 'w-8 h-4',
        thumb: 'w-3 h-3',
        translate: 'translate-x-4',
      },
      md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5',
        translate: 'translate-x-5',
      },
      lg: {
        track: 'w-14 h-7',
        thumb: 'w-6 h-6',
        translate: 'translate-x-7',
      },
    };

    const variants = {
      default: 'bg-(--color-signal-orange)',
      primary: 'bg-(--color-signal-orange)',
      success: 'bg-(--color-success)',
    };

    const labelContent = (
      <div className="flex flex-col">
        {label && (
          <span
            className={cn(
              'text-sm font-medium',
              disabled ? 'text-(--color-muted-gray)' : 'text-(--color-soft-gray)'
            )}
          >
            {label}
          </span>
        )}
        {description && (
          <span className="text-xs text-(--color-muted-gray) mt-0.5">
            {description}
          </span>
        )}
      </div>
    );

    return (
      <div className={cn('relative', className)}>
        <label
          htmlFor={toggleId}
          className={cn(
            'inline-flex items-center gap-3',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            labelPosition === 'left' && 'flex-row-reverse'
          )}
        >
          {/* Hidden Checkbox */}
          <input
            ref={ref}
            type="checkbox"
            id={toggleId}
            disabled={disabled}
            checked={checked}
            className="sr-only peer"
            {...props}
          />

          {/* Track */}
          <div
            className={cn(
              'relative rounded-full transition-colors duration-200',
              sizes[size].track,
              checked
                ? variants[variant]
                : 'bg-(--color-border-gray)',
              disabled && 'opacity-50'
            )}
          >
            {/* Thumb */}
            <div
              className={cn(
                'absolute top-0.5 left-0.5 rounded-full bg-white shadow transition-transform duration-200',
                sizes[size].thumb,
                checked && sizes[size].translate
              )}
            />
          </div>

          {/* Label */}
          {(label || description) && labelContent}
        </label>

        {/* Error */}
        {error && (
          <p className="mt-1 text-sm text-(--color-error)">{error}</p>
        )}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';

export { Toggle };

